import { useTranslation, untranslated } from "@i18n";
import { useCallback, useState, useEffect } from "react";
import type { Dispatch, SetStateAction, SyntheticEvent } from "react";
import type { AutocompleteRenderInputParams, AutocompleteRenderGetTagProps } from "@mui/material/Autocomplete";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Accordion from "@CubeComponents/html/accordion/materialUiAccordian/index";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useDispatch, useSelector } from "react-redux";
import IconComponent from "@CubeComponents/icons";
import { convertDate } from "@Services/luxonHelper";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import SelectDropDown from "@CubeComponents/Mui_Form/SelectDropDown";
import type { SelectEventArgs } from "@CubeComponents/Mui_Form/SelectDropDown";
import AddUsersToMailList from "./AddUsers/AddUsersToMailList";
import { showToastNotification } from "@Reducers/toastSlice";
import ToolTipComponent from "@CubeComponents/html/tooltip";
import { TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS } from "@Services/constants";
import UserNameTagComponent from "@CubeComponents/html/UserNameTagComponent";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import { Box, Chip } from "@mui/material";
import _ from "lodash";
import AutoComplete from "@CubeComponents/Mui_Form/AutoComplete";
import FormPreview from "@Features/portal-settings/components/PortalSetings/Configuration/FormsAndFields/FormTemplate/formPreview";
import { useFormValidator, TEMPLATE_SOURCE_MAIL_SIDEBAR } from "@Features/forms";
import { submitEvent } from "../../../../react-virtualised-tree/eventWrappers";
import Text from "@CubeComponents/html/Text/Text";
import {
  addVisibilityUsersRequest,
  getMailRequest,
  removeVisibilityUsersRequest,
  updateMailCustomFieldsRequest,
  updateMailTagRequest,
} from "@Features/mailbox/api/mailbox.api";
import type { FormTemplateField, QuestionField } from "@ServerTypes/forms/forms.schema";
import type { MailItem, FormTemplate, GetMailResultData } from "@ServerTypes/mails/mails.schema";
import ModuleUsersAccess from "@Components/ModuleUsersAccess";
import { defaultModulesMapper, formCategories } from "@Helpers/constants";
import { RootState } from "@Reducers/index";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";

/** Visibility user entry as returned by the getMail response. */
type MailVisibilityUser = NonNullable<GetMailResultData["visibility"]>[number];

/**
 * Mail details consumed by this sidebar. The server `getMail` projection also
 * returns `tags` and `users` (FULL), which the `getMailResponse` Zod schema does
 * not enumerate, so we extend `GetMailResultData` with the two extra fields the
 * UI actually reads. `users` mirrors the shape `ModuleUsersAccess` consumes.
 */
type MailDetails = GetMailResultData & {
  tags?: string[];
  users?: Array<{ id: string; role: string | number; permissions: number[] }>;
};

/** Local accordion expand/collapse state. */
interface ExpandedAccordionState {
  access: boolean;
  visibility: boolean;
  customFields: boolean;
}

/** Local custom-fields editing state for the sidebar. */
interface CustomFieldsState {
  isFilled: boolean;
  openUpdateCustomFieldModal: boolean;
  isUpdateCustomFielsDisabled: boolean;
  isSaveCustomFieldDiabled: boolean;
  formTemplate: FormTemplate | null;
  isEditing: boolean;
  disableEditCustomFields?: boolean;
  formResetKey: number;
}

interface MailSidebarProps {
  projectId: string;
  mailId: string;
  setOpenMailSidebar: Dispatch<SetStateAction<boolean>>;
  refreshData?: () => void;
}

function MailSidebar({ projectId, mailId, setOpenMailSidebar, refreshData }: MailSidebarProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const getProjectSettingObject = useSelector((state: RootState) => state.getProjectSettingObject);
  const items = [{ text: t("Close"), id: "close" }];
  // `mail` is populated from the getMail response before any strict-field access
  // is reachable at runtime; the empty initial object is asserted to MailDetails.
  const [mail, setMail] = useState<MailDetails>({} as MailDetails);
  const [mailTags, setMailTags] = useState<{ currentTag: string[]; allTags: string[] }>({
    currentTag: [],
    allTags: [],
  });
  const [disableSaveTags, setDisableSaveTags] = useState(true);
  const [openAddTagModal, setOpenAddTagModal] = useState(false);
  const [openAddUserModal, setOpenAddUserModal] = useState(false);
  const [excludedUsers, setExcludedUsers] = useState<string[]>([]);
  const [expandedAccordion, setExpandedAccordion] = useState<ExpandedAccordionState>({
    access: true,
    visibility: true,
    customFields: true,
  });
  const [customFields, setCustomFields] = useState<CustomFieldsState>({
    isFilled: true,
    openUpdateCustomFieldModal: false,
    isUpdateCustomFielsDisabled: false,
    isSaveCustomFieldDiabled: true,
    formTemplate: null,
    isEditing: false,
    formResetKey: 0,
  });
  const formValidator = useFormValidator({});

  const getMailData = useCallback(async () => {
    const response = await getMailRequest({ mailId });
    const mailData: MailItem = (response.data?.result ?? {}) as MailItem;
    setMail(mailData);
    let disableEditCustomFields = true;
    let isCustomFieldsFilled = true;
    if (mailData.formTemplate) {
      isCustomFieldsFilled = !(mailData.isExternal && !mailData.isCustomFieldsFilled);
      const hasMetadataField = mailData.formTemplate.template?.some((section: FormTemplateField) =>
        section.questionFields?.some((question: QuestionField) => question.isMetadata ?? true),
      );
      disableEditCustomFields = !hasMetadataField;
    }
    setCustomFields((prev) => ({
      ...prev,
      formTemplate: mailData?.formTemplate || null,
      isFilled: isCustomFieldsFilled,
      isUpdateCustomFielsDisabled: disableEditCustomFields,
    }));

    if (mailData.tags?.length) {
      setMailTags({ currentTag: mailData.tags, allTags: mailData.tags });
    }

    const excludedUsersArray: string[] = [];
    for (const item of mailData.visibility || []) {
      if (!excludedUsersArray.includes(item._id)) excludedUsersArray.push(item._id);
    }
    setExcludedUsers(excludedUsersArray);
  }, [mailId]);

  useEffect(() => {
    getMailData();
  }, [getMailData]);

  const handleSelectedOption = (args: SelectEventArgs) => {
    if (args.item.properties.id == "close") {
      setOpenMailSidebar(false);
    }
  };

  const getUserAvatar = (
    id?: string,
    _userName?: string,
    _profilePic?: boolean,
    avaterOnly?: boolean,
    showCrossIcon?: boolean | null,
  ) => {
    if (avaterOnly) {
      return <UserNameTagComponent showAvatarOnly id={id} size="LG" isCircle />;
    }
    return showCrossIcon ? (
      <UserNameTagComponent id={id} handleDeleteIconClicked={() => handleRemoveVisibilityUser(id!)} />
    ) : (
      <UserNameTagComponent id={id} />
    );
  };

  const handleManageVisibility = async (selectedUsers: Array<{ _id: string }>) => {
    const selectedUserIds: string[] = [];
    for (const item of selectedUsers) {
      selectedUserIds.push(item._id);
    }
    const response = await addVisibilityUsersRequest({
      mailId: mail._id,
      selectedUserIds,
      projectId,
    });
    if (response.data?.status) {
      getMailData();
      dispatch(
        showToastNotification({
          content: t("The selected user(s) have been added successfully."),
          type: TOAST_TYPE_SUCCESS,
        }),
      );
    } else {
      dispatch(
        showToastNotification({
          content: untranslated(response.data?.errorMessage ?? ""),
          type: TOAST_TYPE_ERROR,
        }),
      );
    }
  };

  const visibilityHeader = () => (
    <div className="flex items-center" data-testid="visibilitySectionMailSidebar">
      <IconComponent name="VisibilityIcon" width="1.5rem" height="1.5rem" />
      <div className="font-semibold text-lg m-2 " data-testid="visibilityText">
        <Text>{t("Visibility")}</Text>
      </div>
    </div>
  );

  const customFieldsHeader = () => (
    <div className="flex items-center" data-testid="customFieldsSectionMailSidebar">
      <IconComponent name="CustomFields" width="1.5rem" height="1.5rem" />
      <div className="font-semibold text-lg m-2 " data-testid="customFieldsText">
        <Text>{t("Fields")}</Text>
      </div>
    </div>
  );
  const handleManage = () => {
    setOpenAddUserModal(true);
  };

  const toggleAccordion = (key: keyof ExpandedAccordionState) => {
    setExpandedAccordion((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleRemoveVisibilityUser = async (id: string) => {
    const response = await removeVisibilityUsersRequest({
      mailId: mail._id,
      selectedUserIds: [id],
      projectId,
    });
    if (response.data?.status) {
      getMailData();
      dispatch(
        showToastNotification({
          content: t("The selected user(s) have been removed successfully."),
          type: TOAST_TYPE_SUCCESS,
        }),
      );
    } else {
      dispatch(
        showToastNotification({
          content: untranslated(response.data?.errorMessage ?? ""),
          type: TOAST_TYPE_ERROR,
        }),
      );
    }
  };

  const visibilityContent = () => (
    <div className="flex flex-col py-5 gap-8" data-testid="visibilityContent">
      <div className="flex justify-start items-center">
        <ButtonComponent
          buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
          onClick={handleManage}
          disabled={!getProjectSettingObject.mailbox?.add?.enable || !Object.keys(mail).length}
          data-testid="visibilityManageBtn"
        >
          <span className="flex items-center" data-testid="manageBtnText">
            <Text>{t("Manage")}</Text>
          </span>
        </ButtonComponent>
      </div>
      <div className="flex justify-start flex-col gap-2" data-testid="visibilityItemMailSidebar">
        {mail.visibility?.map((item: MailVisibilityUser) => {
          const showCrossIcon = getProjectSettingObject.mailbox?.remove?.enable && !item.isDefault;
          return (
            <div key={item._id} className="flex items-center gap-2" data-testid="visibilityItems">
              {getUserAvatar(item._id, item.fullName, true, false, showCrossIcon)}
            </div>
          );
        })}
      </div>
    </div>
  );

  const handleStartEdit = () => {
    setCustomFields((prev) => ({
      ...prev,
      isEditing: true,
    }));
  };

  const handleCancelEdit = () => {
    setCustomFields((prev) => ({
      ...prev,
      isEditing: false,
      formTemplate: mail.formTemplate ?? null,
      formResetKey: prev.formResetKey + 1,
    }));
  };

  const handleCustomFieldsData = (data: FormTemplate["template"]) => {
    setCustomFields((prev) => ({
      ...prev,
      formTemplate: {
        ...prev.formTemplate,
        template: data,
      } as FormTemplate,
    }));
  };

  const handleUpdateCustomFields = async () => {
    if (formValidator.validateAll?.()) {
      const response = await updateMailCustomFieldsRequest({
        mailId: mail._id,
        formTemplate: customFields.formTemplate ?? undefined,
        parentId: mail.parentId,
      });
      if (response.data?.status) {
        dispatch(
          showToastNotification({
            content: t("Fields updated successfully."),
            type: TOAST_TYPE_SUCCESS,
          }),
        );
        setMail((prev) => ({
          ...prev,
          formTemplate: customFields.formTemplate ?? undefined,
        }));
        setCustomFields((prev) => ({
          ...prev,
          isEditing: false,
        }));
        if (refreshData) {
          refreshData();
        }
      } else {
        dispatch(
          showToastNotification({
            content: untranslated(response.data.errorMessage!),
            type: TOAST_TYPE_ERROR,
          }),
        );
      }
    }
  };

  const handleOpenCustomFieldsModal = () => {
    setCustomFields((prev) => ({
      ...prev,
      openUpdateCustomFieldModal: true,
    }));
  };

  const customFieldsContent = () => {
    if (customFields.isFilled) {
      return (
        <div className="flex flex-col py-2 gap-4">
          <div className="flex gap-2">
            {customFields.isEditing ? (
              <>
                <ButtonComponent buttonType={BUTTON_TYPES.GRADIENT_BUTTON} onClick={handleUpdateCustomFields}>
                  <Text>{t("Update")}</Text>
                </ButtonComponent>
                <ButtonComponent buttonType={BUTTON_TYPES.BACKGROUND_BUTTON} onClick={handleCancelEdit}>
                  <Text>{t("Cancel")}</Text>
                </ButtonComponent>
              </>
            ) : (
              <ButtonComponent
                buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
                onClick={handleStartEdit}
                disabled={customFields.isUpdateCustomFielsDisabled}
              >
                <IconComponent name="edit" height="1rem" width="1rem" />
                <Text>{t("Edit")}</Text>
              </ButtonComponent>
            )}
          </div>
          <FormPreview
            key={customFields.formResetKey}
            formData={customFields.formTemplate}
            disabled={!customFields.isEditing}
            fullWidthInput={true}
            resourceType={formCategories.mailBox}
            templateSource={TEMPLATE_SOURCE_MAIL_SIDEBAR}
            handleData={handleCustomFieldsData}
            formValidator={formValidator}
          />
        </div>
      );
    } else {
      return (
        <div className="items-start ps-5 pe-2 py-2 gap-4">
          <ButtonComponent
            disabled={customFields.disableEditCustomFields}
            className="h-8"
            id="edit_external_custom_field"
            buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
            onClick={handleOpenCustomFieldsModal}
          >
            <span className="flex items-center">
              <IconComponent name="edit" height="1rem" width="1rem" />
              <Text>{t("Update")}</Text>
            </span>
          </ButtonComponent>
          <div className="px-4 py-2 mt-4 rounded bg-[rgba(255,207,85,0.25)]">
            <Text>{t("Since this is an externally forwarded email, Fields can be updated one time.")}</Text>
          </div>
        </div>
      );
    }
  };

  const accessHeader = () => (
    <div className="flex items-center">
      <IconComponent name="access" width="1.5rem" height="1.5rem" />
      <div className="font-semibold text-lg m-2 ">
        <Text>{t("Access")}</Text>
      </div>
    </div>
  );

  const accessContent = () => (
    <ModuleUsersAccess
      module={defaultModulesMapper.MAILBOX}
      resourceId={mail._id}
      users={(mail?.users ?? []) as unknown as Array<{ id: string; role: string; permissions: number[] }>}
      projectId={projectId}
    />
  );

  const accordionMapper: Array<{
    id: keyof ExpandedAccordionState;
    header: () => React.ReactElement;
    content: () => React.ReactElement;
    dataTestId: string;
  }> = [
    {
      id: "access",
      header: accessHeader,
      content: accessContent,
      dataTestId: "accessWithExpandIcon",
    },
    {
      id: "visibility",
      header: visibilityHeader,
      content: visibilityContent,
      dataTestId: "visibilityFieldWithExpandIcon",
    },
    ...(mail.formTemplate
      ? [
          {
            id: "customFields" as const,
            header: customFieldsHeader,
            content: customFieldsContent,
            dataTestId: "customeFieldWithExpandIcon",
          },
        ]
      : []),
  ];

  const getSidebarContent = () =>
    accordionMapper.map((data) => (
      <Accordion
        className="px-5"
        onChange={() => toggleAccordion(data.id)}
        expanded={expandedAccordion[data.id]}
        key={data.id}
      >
        <AccordionSummary style={{ padding: "0" }} expandIcon={<ExpandMoreIcon />} data-testid={data.dataTestId}>
          {data.header()}
        </AccordionSummary>
        <AccordionDetails>{data.content()}</AccordionDetails>
      </Accordion>
    ));
  const handleAddTag = () => {
    setOpenAddTagModal(true);
  };

  const SidebarData = () => (
    <div className="w-120 h-full flex flex-col overflow-auto" data-testid="mailProperties">
      <div className="bg-surface_white w-full h-full flex flex-1 flex-col p-5 gap-4">
        <div className="flex items-center justify-between">
          <div className="border-solid border border-black py-1 px-3" data-testid="mailNumber">
            #{mail.mailNumber}
          </div>
          <div className="flex items-center justify-end gap-2">
            <SelectDropDown
              select={(args) => handleSelectedOption(args)}
              items={items}
              cssClass="e-caret-hide"
              className="p-0"
              data-testid="mailSideBarDropdownBtn"
            >
              <IconComponent name="threedots" className="cursor-pointer p-0" width="1.25rem" height="1.25rem" />
            </SelectDropDown>
          </div>
        </div>

        <div className="flex items-center gap-2" data-testid="mailSideBarHeader">
          <IconComponent name="Inbox" width="1.25rem" height="1.25rem" />
          <ToolTipComponent content={untranslated(mail.subject ?? "")} position="bottom">
            <div className="font-semibold text-2xl truncate" data-testid="nameMailSideBar">
              {mail.subject}
            </div>
          </ToolTipComponent>
        </div>
        <div className="flex items-center gap-2" data-testid="mailSentByDetailsMailSideBar">
          {getUserAvatar(mail.sentBy?._id, mail.sentBy?.name, true, false, null)}
          <Text>{t("on")}</Text>
          <div className="font-semibold text-sm" data-testid="createdDateSideBar">
            {convertDate(Number(mail.createdDate), "dateForText")}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Box
            className="flex items-center border-solid border border-gray-100 rounded-lg p-2 gap-1 cursor-pointer"
            data-testid="addTagSectionMailSideBar"
            onClick={handleAddTag}
            onKeyDown={submitEvent(handleAddTag)}
          >
            <IconComponent name="add-icon" width="1.25rem" height="1.25rem" />
            <div className="font-medium text-sm" data-testid="addTag">
              <Text>{t("Add Tags")}</Text>
            </div>
          </Box>
        </div>
      </div>
      <div className="bg-surface_white overflow-auto w-full h-full flex gap-1 justify-between border-solid border border-gray-100">
        <div className="w-full h-full flex flex-col px-2 overflow-auto">{mail._id && getSidebarContent()}</div>

        <div className="h-full p-4 gap-4 border-solid border border-s-1 border-t-0 border-gray-100">
          <div className="flex flex-col gap-4" data-testid="VisibilityIconsMailSidebar">
            <IconComponent
              name="access"
              className="cursor-pointer"
              onClick={() => toggleAccordion("access")}
              width="1.75rem"
              height="1.75rem"
            />
            <IconComponent
              name="VisibilityIcon"
              className="cursor-pointer"
              onClick={() => toggleAccordion("visibility")}
              width="1.75rem"
              height="1.75rem"
            />
            {mail.formTemplate && (
              <IconComponent
                name="CustomFields"
                className="cursor-pointer"
                onClick={() => toggleAccordion("customFields")}
                width="1.75rem"
                height="1.75rem"
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const handleCloseModal = () => {
    setDisableSaveTags(true);
    setMailTags((prev) => ({
      ...prev,
      currentTag: prev.allTags,
    }));
    setOpenAddTagModal(false);
  };

  const handleCloseCustomFieldsModal = () => {
    setCustomFields((prev) => ({
      ...prev,
      openUpdateCustomFieldModal: false,
      isSaveCustomFieldDiabled: true,
      formTemplate: mail.formTemplate ?? null,
    }));
  };

  const handleChangeCustomFields = (data: FormTemplate["template"]) => {
    let isCustomFieldsUpdted = false;
    if (_.isEqual(data, mail.formTemplate?.template)) {
      isCustomFieldsUpdted = true;
    }
    setCustomFields((prev) => ({
      ...prev,
      formTemplate: {
        ...prev.formTemplate,
        template: data,
      } as FormTemplate,
      isSaveCustomFieldDiabled: isCustomFieldsUpdted,
    }));
  };

  const editCustomFieldsModal = () => (
    <ModalStateDialogComponent
      headerTitle={t("Update Fields")}
      modelContent={editCustomFieldsContent()}
      footerContent={editCustomFieldsFooter()}
      isVisible={true}
      width="30%"
      height="50%"
      onCancel={handleCloseCustomFieldsModal}
    />
  );

  const handleSaveCustomFields = async () => {
    if (formValidator.validateAll?.()) {
      setCustomFields((prev) => ({
        ...prev,
        isSaveCustomFieldDiabled: true,
      }));
      const response = await updateMailCustomFieldsRequest({
        mailId: mail._id,
        formTemplate: customFields.formTemplate ?? undefined,
        parentId: mail.parentId,
      });
      if (response.data.status) {
        dispatch(
          showToastNotification({
            content: t("Successfully Updated Fields."),
            type: TOAST_TYPE_SUCCESS,
          }),
        );
        setMail((prev) => ({
          ...prev,
          formTemplate: customFields.formTemplate ?? undefined,
        }));
        if (refreshData) {
          refreshData();
        }
      } else {
        dispatch(
          showToastNotification({
            content: untranslated(response.data.errorMessage!),
            type: TOAST_TYPE_ERROR,
          }),
        );
      }
      setCustomFields((prev) => ({
        ...prev,
        openUpdateCustomFieldModal: false,
        isFilled: response.data.status,
      }));
    }
  };

  const editCustomFieldsContent = () => (
    <div className="flex flex-col gap-4 overflow-auto">
      <div className="p-4 rounded bg-[rgba(255,207,85,0.25)]">
        <Text>{t("Fields once saved cannot be edited again.")}</Text>
      </div>
      <div>
        <FormPreview
          formData={customFields.formTemplate}
          disabled={false}
          fullWidthInput
          resourceType={formCategories.mailBox}
          templateSource={TEMPLATE_SOURCE_MAIL_SIDEBAR}
          handleData={handleChangeCustomFields}
          formValidator={formValidator}
        />
      </div>
    </div>
  );

  const editCustomFieldsFooter = () => (
    <div className="flex gap-4 mt-4">
      <ButtonComponent
        buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
        onClick={handleSaveCustomFields}
        disabled={customFields.isSaveCustomFieldDiabled}
      >
        <Text>{t("Save")}</Text>
      </ButtonComponent>
      <ButtonComponent buttonType={BUTTON_TYPES.BACKGROUND_BUTTON} onClick={handleCloseCustomFieldsModal}>
        <Text>{t("Discard")}</Text>
      </ButtonComponent>
    </div>
  );

  const addNoteModalHeader = () => {
    return (
      <div className="w-full flex flex-row justify-between items-center rounded-lg">
        <div className="text-2xl text-primary_text">
          <Text>{t("Add Tags")}</Text>
        </div>
      </div>
    );
  };
  const handleChange = (_event: SyntheticEvent, newValue: string[]) => {
    const uniqueValues = newValue;
    const disableSave = _.isEqual(uniqueValues, mailTags.allTags);
    setMailTags((prev) => ({
      ...prev,
      currentTag: uniqueValues,
    }));
    setDisableSaveTags(disableSave);
  };

  const getTagColor = (tag: string) => {
    if (!tag) return `var(--system-colour-1)`;
    let hash = 0;
    for (let i = 0; i < tag.length; i++) {
      hash = (tag.codePointAt(i) ?? 0) + ((hash << 5) - hash);
    }
    const colorIndex = (Math.abs(hash) % 46) + 1;
    return `var(--system-colour-${colorIndex})`;
  };

  const addNoteModalContent = () => (
    <div className="w-full">
      <AutoComplete
        multiple
        freeSolo
        options={[]}
        value={mailTags.currentTag}
        onChange={handleChange}
        filterSelectedOptions
        renderTags={(value: string[], getTagProps: AutocompleteRenderGetTagProps) =>
          value.map((option: string, index: number) => {
            const { key, ...tagProps } = getTagProps({ index });
            return (
              <Chip
                deleteIcon={<IconComponent name="crossWhite" width="1rem" height="1rem" />}
                variant="outlined"
                key={key}
                sx={{
                  backgroundColor: getTagColor(option),
                  color: "white",
                  paddingX: "0.5rem",
                  gap: "0.5rem",
                }}
                label={option}
                {...tagProps}
              />
            );
          })
        }
        renderInput={(params: AutocompleteRenderInputParams) => (
          <TextFieldComponent
            {...params}
            variant="outlined"
            placeholder={mailTags.currentTag.length == 0 ? t("Enter tags and press Enter") : ""}
          />
        )}
      />
    </div>
  );

  const handleSaveTag = async () => {
    const response = await updateMailTagRequest({
      tags: mailTags.currentTag,
      mailId,
    });
    if (response?.data?.status) {
      setMailTags((prev) => ({
        ...prev,
        allTags: prev.currentTag,
      }));
      dispatch(
        showToastNotification({
          content: t("Tag updated Successfully!"),
          type: TOAST_TYPE_SUCCESS,
        }),
      );
      if (refreshData) {
        refreshData();
      }
    } else {
      setMailTags((prev) => ({
        ...prev,
        currentTag: prev.allTags,
      }));
      dispatch(
        showToastNotification({
          content: t("Failed to Update Tag!"),
          type: TOAST_TYPE_ERROR,
        }),
      );
    }
    setDisableSaveTags(true);
    setOpenAddTagModal(false);
  };

  const addNoteModalFooter = () => {
    return (
      <div className="flex gap-4">
        <ButtonComponent buttonType={BUTTON_TYPES.GRADIENT_BUTTON} onClick={handleSaveTag} disabled={disableSaveTags}>
          <Text>{t("Save")}</Text>
        </ButtonComponent>
        <ButtonComponent buttonType={BUTTON_TYPES.BACKGROUND_BUTTON} onClick={handleCloseModal}>
          <Text>{t("Cancel")}</Text>
        </ButtonComponent>
      </div>
    );
  };

  return (
    <>
      {openAddUserModal ? (
        <AddUsersToMailList
          closeModal={() => setOpenAddUserModal(false)}
          projectId={projectId}
          excludedUsers={excludedUsers}
          handleNext={handleManageVisibility}
        />
      ) : null}
      {openAddTagModal && (
        <ModalStateDialogComponent
          headerTitle={addNoteModalHeader()}
          modelContent={addNoteModalContent()}
          footerContent={addNoteModalFooter()}
          isVisible={true}
          width="30%"
          onCancel={handleCloseModal}
        />
      )}
      {mail ? SidebarData() : null}
      {customFields.openUpdateCustomFieldModal && editCustomFieldsModal()}
    </>
  );
}

export default MailSidebar;
